if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          navigationButtons: this.querySelectorAll('[data-gallery-direction]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupDeferredImageLoading();
        this.elements.viewer.slider?.addEventListener(
          'pointerdown',
          () => {
            this.cancelGalleryAnimation();
            this.prepareNavigationTarget('next', 'low');
          },
          { passive: true }
        );
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.elements.navigationButtons.forEach((button) => {
          button.addEventListener('click', this.navigateMedia.bind(this, button.dataset.galleryDirection));
          button.addEventListener(
            'pointerenter',
            () => this.prepareNavigationTarget(button.dataset.galleryDirection, 'low'),
            { passive: true }
          );
          button.addEventListener('focus', () =>
            this.prepareNavigationTarget(button.dataset.galleryDirection, 'low')
          );
        });
        this.updateNavigationState();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupDeferredImageLoading() {
        const deferredImages = this.querySelectorAll('img[data-ltp-deferred-image][data-ltp-deferred-src]');
        if (!deferredImages.length) return;

        if (!('IntersectionObserver' in window)) return;

        this.deferredImageObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.loadDeferredImage(entry.target);
              this.deferredImageObserver.unobserve(entry.target);
            });
          },
          {
            root: this.elements.viewer.slider || this.elements.viewer,
            rootMargin: '0px 50%',
          }
        );

        deferredImages.forEach((image) => this.deferredImageObserver.observe(image));
      }

      getDeferredImage(imageOrContainer) {
        return imageOrContainer?.matches?.('img[data-ltp-deferred-image]')
          ? imageOrContainer
          : imageOrContainer?.querySelector?.('img[data-ltp-deferred-image]');
      }

      loadDeferredImage(imageOrContainer, fetchPriority = 'auto') {
        const image = this.getDeferredImage(imageOrContainer);
        if (!image) return image;

        image.fetchPriority = fetchPriority;
        if (!image.dataset.ltpDeferredSrc) return image;
        if (image.dataset.ltpDeferredSrcset) image.srcset = image.dataset.ltpDeferredSrcset;
        image.src = image.dataset.ltpDeferredSrc;
        delete image.dataset.ltpDeferredSrcset;
        delete image.dataset.ltpDeferredSrc;
        this.deferredImageObserver?.unobserve(image);
        return image;
      }

      prepareMediaImage(imageOrContainer, fetchPriority = 'auto') {
        const image = this.getDeferredImage(imageOrContainer);
        if (!image) return Promise.resolve(null);

        image.loading = 'eager';
        this.loadDeferredImage(image, fetchPriority);
        if (!image.ltpReadyPromise) {
          if (image.decode) {
            image.ltpReadyPromise = image.decode().catch(() => undefined).then(() => image);
          } else if (image.complete) {
            image.ltpReadyPromise = Promise.resolve(image);
          } else {
            image.ltpReadyPromise = new Promise((resolve) => {
              const finish = () => resolve(image);
              image.addEventListener('load', finish, { once: true });
              image.addEventListener('error', finish, { once: true });
            });
          }
        }
        return image.ltpReadyPromise;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateNavigationState();
      }

      getAvailableMediaTargets() {
        return Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []).filter(
          (thumbnail) => window.getComputedStyle(thumbnail).display !== 'none'
        );
      }

      getNavigationTarget(direction) {
        const targets = this.getAvailableMediaTargets();
        if (targets.length < 2) return null;

        const activeMediaId = this.elements.viewer.querySelector('[data-media-id].is-active')?.dataset.mediaId;
        const currentIndex = Math.max(
          0,
          targets.findIndex((thumbnail) => thumbnail.dataset.target === activeMediaId)
        );
        const directionOffset = direction === 'previous' ? -1 : 1;
        const nextIndex = (currentIndex + directionOffset + targets.length) % targets.length;
        const target = targets[nextIndex];
        const targetMedia = this.elements.viewer.querySelector(`[data-media-id="${target.dataset.target}"]`);
        return { target, targetMedia };
      }

      prepareNavigationTarget(direction, fetchPriority = 'low') {
        const navigationTarget = this.getNavigationTarget(direction);
        if (!navigationTarget) return Promise.resolve(null);
        return this.prepareMediaImage(navigationTarget.targetMedia, fetchPriority);
      }

      navigateMedia(direction) {
        const navigationTarget = this.getNavigationTarget(direction);
        if (!navigationTarget) return;
        const { target, targetMedia } = navigationTarget;
        const targetImage = this.getDeferredImage(targetMedia);
        if (targetImage) {
          const navigationToken = (this.navigationToken || 0) + 1;
          this.navigationToken = navigationToken;
          this.prepareMediaImage(targetMedia, 'auto').then(() => {
            if (navigationToken !== this.navigationToken) return;
            this.setActiveMedia(target.dataset.target, false, 240);
          });
          return;
        }

        this.setActiveMedia(target.dataset.target, false);
      }

      updateNavigationState() {
        if (!this.elements.navigationButtons.length) return;

        const targets = this.getAvailableMediaTargets();
        const activeMediaId = this.elements.viewer.querySelector('[data-media-id].is-active')?.dataset.mediaId;
        const currentIndex = targets.findIndex((thumbnail) => thumbnail.dataset.target === activeMediaId);
        const previousButton = Array.from(this.elements.navigationButtons).find(
          (button) => button.dataset.galleryDirection === 'previous'
        );
        const nextButton = Array.from(this.elements.navigationButtons).find(
          (button) => button.dataset.galleryDirection === 'next'
        );

        const navigationUnavailable = targets.length < 2 || currentIndex === -1;
        if (previousButton) previousButton.disabled = navigationUnavailable;
        if (nextButton) nextButton.disabled = navigationUnavailable;
      }

      setActiveMedia(mediaId, prepend, transitionDuration = 0, startImmediately = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.loadDeferredImage(activeMedia);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        const positionActiveMedia = () => {
          if (!this.mql.matches || this.elements.thumbnails) {
            if (transitionDuration > 0) {
              this.animateGalleryScroll(activeMedia, transitionDuration);
            } else {
              this.cancelGalleryAnimation();
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        };
        if (startImmediately) {
          positionActiveMedia();
        } else {
          window.setTimeout(positionActiveMedia);
        }
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.updateNavigationState();
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      animateGalleryScroll(activeMedia, duration) {
        const slider = activeMedia.parentElement;
        const currentAnimation = this.galleryAnimation;
        let scrollStyles;
        if (currentAnimation) {
          cancelAnimationFrame(currentAnimation.frame);
          scrollStyles = currentAnimation.scrollStyles;
          this.galleryAnimation = null;
        } else {
          scrollStyles = {
            behavior: slider.style.getPropertyValue('scroll-behavior'),
            behaviorPriority: slider.style.getPropertyPriority('scroll-behavior'),
            snapType: slider.style.scrollSnapType,
          };
          slider.style.setProperty('scroll-behavior', 'auto', 'important');
          slider.style.scrollSnapType = 'none';
        }

        const startLeft = slider.scrollLeft;
        const targetLeft = this.getGalleryScrollTarget(activeMedia, slider);
        const distance = targetLeft - startLeft;

        const restore = () => {
          if (scrollStyles.behavior) {
            slider.style.setProperty('scroll-behavior', scrollStyles.behavior, scrollStyles.behaviorPriority);
          } else {
            slider.style.removeProperty('scroll-behavior');
          }
          slider.style.scrollSnapType = scrollStyles.snapType;
          if (this.galleryAnimation?.restore === restore) this.galleryAnimation = null;
        };

        if (Math.abs(distance) < 1 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          slider.scrollLeft = targetLeft;
          restore();
          return;
        }

        const startTime = performance.now();
        const step = (time) => {
          const progress = Math.min(Math.max((time - startTime) / duration, 0), 1);
          const easedProgress = 1 - Math.pow(1 - progress, 3);
          slider.scrollLeft = startLeft + distance * easedProgress;
          if (progress < 1) {
            this.galleryAnimation.frame = requestAnimationFrame(step);
            return;
          }
          slider.scrollLeft = targetLeft;
          restore();
        };

        this.galleryAnimation = {
          frame: requestAnimationFrame(step),
          restore,
          scrollStyles,
        };
      }

      getGalleryScrollTarget(activeMedia, slider) {
        if (this.mql.matches) return activeMedia.offsetLeft;

        const sliderStyles = window.getComputedStyle(slider);
        const mediaStyles = window.getComputedStyle(activeMedia);
        const scrollPadding =
          Number.parseFloat(sliderStyles.scrollPaddingInlineStart) ||
          Number.parseFloat(sliderStyles.scrollPaddingLeft) ||
          0;
        const scrollMargin =
          Number.parseFloat(mediaStyles.scrollMarginInlineStart) ||
          Number.parseFloat(mediaStyles.scrollMarginLeft) ||
          0;
        const snapStart = activeMedia.offsetLeft - scrollPadding - scrollMargin;
        return Math.min(Math.max(snapStart, 0), slider.scrollWidth - slider.clientWidth);
      }

      cancelGalleryAnimation() {
        if (!this.galleryAnimation) return;
        cancelAnimationFrame(this.galleryAnimation.frame);
        this.galleryAnimation.restore();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete && image.naturalWidth) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
